import { fitKeyRow, isKeyGroup } from './exportLabelKeyTable'
import { clusterPlacement, keyTagPlacement, rimSpot } from './exportLabelSolver'
import {
  CLUSTER_MARKER_RADIUS_PX,
  IExportLabelPlacement,
  IExportLabelResult,
  IExportLeader,
  KEY_BADGE_RADIUS_PX,
  KEY_BADGE_WEIGHT,
  LabelMeasure,
} from './exportLabelTypes'

// A white band under the map, in layout px: title and date on the left; legend, scale bar and
// north arrow on the right. It never sits over the map, so it can never hide what it describes.
// Every size below is at ×1; the band scales them all as one, line weights aside.
const PAGE_BAND_PX = 64
export const PAGE_BAND_RULE_PX = 1
const BAND_PADDING_PX = 16
const BLOCK_GAP_PX = 24
const LINE_HEIGHT = 1.2
export const PAGE_BAND_TITLE_PX = 14
const TITLE_TEXT = { sizePx: PAGE_BAND_TITLE_PX, weight: 600 }
const SMALL_TEXT = { sizePx: 10, weight: 500 }
const TITLE_DATE_GAP_PX = 4
const LEGEND_ITEM_GAP_PX = 16
const LEGEND_SYMBOL_GAP_PX = 6
const LEADER_SAMPLE_PX = { width: 16, rise: 8, dotInset: 4 }
const SCALE_BAR_MAX_PX = 120
const SCALE_BAR_HEIGHT_PX = 4
const SCALE_LABEL_GAP_PX = 4
const NORTH_ARROW_RADIUS_PX = 11
const NORTH_LETTER_GAP_PX = 7
const NICE_STEPS = [5, 2, 1]
const GROUP_TAG_SAMPLE = 'A'
// A sample count, never a letter: an N would read as the north arrow's.
const CLUSTER_COUNT_SAMPLE = '3'
// The room the title keeps beside the legend, scaled with the band, so a typical project and view
// name (about 30 characters) still fits at the largest scale the sheet allows.
const MIN_TITLE_PX = 240

export type PageLegendKind = 'cluster' | 'keyTag' | 'truePosition'

const PAGE_LEGEND_KINDS: PageLegendKind[] = [
  'cluster',
  'keyTag',
  'truePosition',
]

export interface IPageBandText {
  text: string
  x: number
  top: number
  sizePx: number
  lineHeightPx: number
  weight: number
  align: 'start' | 'middle'
  maxWidthPx: number
}

export interface IPageLegendItem {
  kind: PageLegendKind
  symbol: IExportLabelPlacement | null
  // The letter on a counter's rim, shown once groups are lettered into the label key.
  tag: IExportLabelPlacement | null
  leader: IExportLeader | null
  label: IPageBandText
}

export interface IPageScaleBar {
  x: number
  y: number
  widthPx: number
  heightPx: number
  label: IPageBandText
}

export interface IPagePoint {
  x: number
  y: number
}

export interface IPageNorthArrow {
  dark: IPagePoint[]
  light: IPagePoint[]
  letter: IPageBandText
}

export interface IPageBand {
  top: number
  width: number
  height: number
  title: IPageBandText
  date: IPageBandText
  legend: IPageLegendItem[]
  scaleBar: IPageScaleBar | null
  northArrow: IPageNorthArrow
}

export interface IScaleLength {
  metres: number
  value: number
  unit: 'm' | 'km' | 'ft' | 'mi'
}

export interface IPageBandCopy {
  title: string
  date: string
  north: string
  legend: Record<PageLegendKind, string>
  clusterKeyedLegend: string
  scaleLength: (length: IScaleLength) => string
}

const METRES_PER_FOOT = 0.3048
const FEET_PER_MILE = 5280

// The longest 1-2-5 length up to `max`, so the bar reads at a glance.
function niceLength(max: number): number {
  let magnitude = 10 ** Math.floor(Math.log10(max))
  if (magnitude > max) {
    magnitude /= 10
  }
  return (
    NICE_STEPS.map(step => step * magnitude).find(l => l <= max) ?? magnitude
  )
}

// In the paper's units, as the export's other readouts: metres and kilometres, else feet and miles.
export function scaleBarLength(
  metresPerPx: number,
  maxPx: number,
  isMetric: boolean,
): IScaleLength {
  const maxMetres = metresPerPx * maxPx
  if (isMetric) {
    const metres = niceLength(maxMetres)
    return metres >= 1000
      ? { metres, value: metres / 1000, unit: 'km' }
      : { metres, value: metres, unit: 'm' }
  }
  const maxFeet = maxMetres / METRES_PER_FOOT
  if (maxFeet < FEET_PER_MILE) {
    const feet = niceLength(maxFeet)
    return { metres: feet * METRES_PER_FOOT, value: feet, unit: 'ft' }
  }
  const miles = niceLength(maxFeet / FEET_PER_MILE)
  return {
    metres: miles * FEET_PER_MILE * METRES_PER_FOOT,
    value: miles,
    unit: 'mi',
  }
}

// Clockwise degrees from page-up to true north: the map turned by `bearing` turns north back. A
// tilt foreshortens the north-south axis, so under pitch the angle holds at the map's centre only.
export function northArrowAngle(bearing: number, pitch = 0): number {
  if (pitch === 0) {
    return ((-bearing % 360) + 360) % 360
  }
  const b = (bearing * Math.PI) / 180
  const p = (pitch * Math.PI) / 180
  const angle =
    (Math.atan2(-Math.sin(b), Math.cos(b) * Math.cos(p)) * 180) / Math.PI
  return ((angle % 360) + 360) % 360
}

export const bandHeightPx = (scale: number): number =>
  Math.ceil(PAGE_BAND_PX * scale)

export function pageLegendKinds(
  labels: IExportLabelResult | null,
): PageLegendKind[] {
  if (!labels) {
    return []
  }
  const shown: Record<PageLegendKind, boolean> = {
    cluster: labels.placements.some(p => p.clusterCount != null),
    keyTag: labels.key.some(entry => !isKeyGroup(entry)),
    truePosition: labels.leaders.length > 0,
  }
  return PAGE_LEGEND_KINDS.filter(kind => shown[kind])
}

const scaledText = (
  style: { sizePx: number; weight: number },
  scale: number,
): { sizePx: number; weight: number } => ({
  sizePx: style.sizePx * scale,
  weight: style.weight,
})

const textLine = (
  text: string,
  style: { sizePx: number; weight: number },
  x: number,
  top: number,
  maxWidthPx: number,
  align: IPageBandText['align'] = 'start',
): IPageBandText => ({
  text,
  x,
  top,
  sizePx: style.sizePx,
  lineHeightPx: style.sizePx * LINE_HEIGHT,
  weight: style.weight,
  align,
  maxWidthPx,
})

function rotate(
  point: IPagePoint,
  centre: IPagePoint,
  angleDeg: number,
): IPagePoint {
  const radians = (angleDeg * Math.PI) / 180
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)
  return {
    x: centre.x + point.x * cos - point.y * sin,
    y: centre.y + point.x * sin + point.y * cos,
  }
}

const NORTH_ARROW_EXTENT_PX =
  NORTH_ARROW_RADIUS_PX +
  NORTH_LETTER_GAP_PX +
  (SMALL_TEXT.sizePx * LINE_HEIGHT) / 2

function layoutNorthArrow(
  centre: IPagePoint,
  bearing: number,
  pitch: number,
  letter: string,
  scale: number,
): IPageNorthArrow {
  const r = NORTH_ARROW_RADIUS_PX * scale
  const angle = northArrowAngle(bearing, pitch)
  const at = (x: number, y: number): IPagePoint =>
    rotate({ x, y }, centre, angle)
  const tip = at(0, -r)
  const notch = at(0, r / 2)
  const letterCentre = at(0, -(r + NORTH_LETTER_GAP_PX * scale))
  const sizePx = SMALL_TEXT.sizePx * scale
  return {
    dark: [tip, at(-0.6 * r, r), notch],
    light: [tip, notch, at(0.6 * r, r)],
    letter: textLine(
      letter,
      { sizePx, weight: KEY_BADGE_WEIGHT },
      letterCentre.x,
      letterCentre.y - (sizePx * LINE_HEIGHT) / 2,
      2 * r,
      'middle',
    ),
  }
}

function legendSymbolWidth(kind: PageLegendKind): number {
  switch (kind) {
    case 'cluster':
      return 2 * CLUSTER_MARKER_RADIUS_PX
    case 'keyTag':
      return 2 * KEY_BADGE_RADIUS_PX
    default:
      return LEADER_SAMPLE_PX.width
  }
}

// The legend's width at `scale`, from each row's text width at that scale.
const legendWidth = (
  kinds: PageLegendKind[],
  textWidths: number[],
  scale: number,
): number =>
  kinds.reduce(
    (sum, kind, index) =>
      sum +
      (legendSymbolWidth(kind) +
        LEGEND_SYMBOL_GAP_PX +
        (index ? LEGEND_ITEM_GAP_PX : 0)) *
        scale +
      textWidths[index],
    0,
  )

// Samples are the map's own symbols at the band's scale, so they match the map at the same scale.
const samplePlacement = (
  kind: 'cluster' | 'keyTag',
  x: number,
  midY: number,
  text: string,
  scale: number,
): IExportLabelPlacement => {
  const size = legendSymbolWidth(kind) * scale
  const box = { x, y: midY - size / 2, width: size, height: size }
  const id = `legend-${kind}`
  return kind === 'cluster'
    ? clusterPlacement(id, box, 0, scale, text)
    : keyTagPlacement(
        id,
        'workflowPin',
        text,
        box,
        { x: x + size / 2, y: midY },
        scale,
      )
}

// The group tag's sample, on the swatch disk's rim as on the map.
const sampleGroupTag = (
  disk: IExportLabelPlacement,
  scale: number,
): IExportLabelPlacement => {
  const spot = rimSpot(disk.anchor, CLUSTER_MARKER_RADIUS_PX * scale)
  return samplePlacement(
    'keyTag',
    spot.x - KEY_BADGE_RADIUS_PX * scale,
    spot.y,
    GROUP_TAG_SAMPLE,
    scale,
  )
}

function layoutLegend(
  kinds: PageLegendKind[],
  isClusterKeyed: boolean,
  right: number,
  midY: number,
  copy: IPageBandCopy,
  measure: LabelMeasure,
  scale: number,
): { items: IPageLegendItem[]; left: number } {
  const text = scaledText(SMALL_TEXT, scale)
  const textOf = (kind: PageLegendKind): string =>
    kind === 'cluster' && isClusterKeyed
      ? copy.clusterKeyedLegend
      : copy.legend[kind]
  const widths = kinds.map(kind =>
    measure(textOf(kind), text.sizePx, text.weight, 0),
  )
  const left = right - legendWidth(kinds, widths, scale)
  const textTop = midY - (text.sizePx * LINE_HEIGHT) / 2
  let x = left
  const items = kinds.map((kind, index) => {
    const symbolX = x
    const textX =
      symbolX + (legendSymbolWidth(kind) + LEGEND_SYMBOL_GAP_PX) * scale
    x = textX + widths[index] + LEGEND_ITEM_GAP_PX * scale
    const label = textLine(textOf(kind), text, textX, textTop, widths[index])
    if (kind === 'truePosition') {
      const from = {
        x: symbolX + LEADER_SAMPLE_PX.dotInset * scale,
        y: midY + (LEADER_SAMPLE_PX.rise * scale) / 2,
      }
      const to = {
        x: symbolX + LEADER_SAMPLE_PX.width * scale,
        y: midY - (LEADER_SAMPLE_PX.rise * scale) / 2,
      }
      return {
        kind,
        symbol: null,
        tag: null,
        leader: { key: 'legend', from, to },
        label,
      }
    }
    const symbolText = kind === 'cluster' ? CLUSTER_COUNT_SAMPLE : '1'
    const symbol = samplePlacement(kind, symbolX, midY, symbolText, scale)
    return {
      kind,
      symbol,
      tag:
        kind === 'cluster' && isClusterKeyed
          ? sampleGroupTag(symbol, scale)
          : null,
      leader: null,
      label,
    }
  })
  return { items, left: kinds.length ? left : right + BLOCK_GAP_PX * scale }
}

function layoutScaleBar(
  metresPerPx: number,
  isMetric: boolean,
  right: number,
  midY: number,
  copy: IPageBandCopy,
  scale: number,
): IPageScaleBar {
  const maxPx = SCALE_BAR_MAX_PX * scale
  const heightPx = SCALE_BAR_HEIGHT_PX * scale
  const gapPx = SCALE_LABEL_GAP_PX * scale
  const text = scaledText(SMALL_TEXT, scale)
  const length = scaleBarLength(metresPerPx, maxPx, isMetric)
  const widthPx = length.metres / metresPerPx
  const labelHeight = text.sizePx * LINE_HEIGHT
  const top = midY - (labelHeight + gapPx + heightPx) / 2
  const x = right - widthPx
  return {
    x,
    y: top + labelHeight + gapPx,
    widthPx,
    heightPx,
    label: textLine(
      copy.scaleLength(length),
      text,
      x + widthPx / 2,
      top,
      maxPx,
      'middle',
    ),
  }
}

// The largest scale at which the band still keeps the minimum title room on a sheet this wide,
// never below ×1. It reserves every legend row in its longest wording, whatever the labels show,
// so it never depends on the label solve the sheet feeds.
export function pageBandFit(
  width: number,
  copy: Pick<IPageBandCopy, 'legend' | 'clusterKeyedLegend'>,
  measure: LabelMeasure,
): number {
  const textWidth = (text: string): number =>
    measure(text, SMALL_TEXT.sizePx, SMALL_TEXT.weight, 0)
  const legendTextWidths = PAGE_LEGEND_KINDS.map(kind =>
    kind === 'cluster'
      ? Math.max(
          textWidth(copy.legend.cluster),
          textWidth(copy.clusterKeyedLegend),
        )
      : textWidth(copy.legend[kind]),
  )
  const blocksAtOne =
    2 * BAND_PADDING_PX +
    2 * NORTH_ARROW_EXTENT_PX +
    3 * BLOCK_GAP_PX +
    SCALE_BAR_MAX_PX +
    legendWidth(PAGE_LEGEND_KINDS, legendTextWidths, 1)
  return Math.max(1, width / (blocksAtOne + MIN_TITLE_PX))
}

// `top` is where the band starts on the sheet: the map's height. `metresPerPx` is null until the
// camera is known, and the scale bar waits for it.
export function layoutPageBand(input: {
  top: number
  width: number
  scale: number
  copy: IPageBandCopy
  labels: IExportLabelResult | null
  metresPerPx: number | null
  isMetric: boolean
  bearing: number
  pitch: number
  measure: LabelMeasure
}): IPageBand {
  const {
    top,
    width,
    scale,
    copy,
    labels,
    metresPerPx,
    isMetric,
    bearing,
    pitch,
    measure,
  } = input
  const height = bandHeightPx(scale)
  const midY = top + height / 2
  const paddingPx = BAND_PADDING_PX * scale
  const blockGapPx = BLOCK_GAP_PX * scale
  const arrowExtentPx = NORTH_ARROW_EXTENT_PX * scale

  const arrowCentre = { x: width - paddingPx - arrowExtentPx, y: midY }
  const northArrow = layoutNorthArrow(
    arrowCentre,
    bearing,
    pitch,
    copy.north,
    scale,
  )
  let right = arrowCentre.x - arrowExtentPx - blockGapPx

  const scaleBar = metresPerPx
    ? layoutScaleBar(metresPerPx, isMetric, right, midY, copy, scale)
    : null
  if (scaleBar) {
    right -= (SCALE_BAR_MAX_PX + BLOCK_GAP_PX) * scale
  }

  const legend = layoutLegend(
    pageLegendKinds(labels),
    !!labels?.key.some(isKeyGroup),
    right,
    midY,
    copy,
    measure,
    scale,
  )
  right = legend.left - blockGapPx

  const titleText = scaledText(TITLE_TEXT, scale)
  const dateText = scaledText(SMALL_TEXT, scale)
  const titleMaxPx = Math.max(0, right - paddingPx)
  const titleHeight = titleText.sizePx * LINE_HEIGHT
  const titleTop =
    midY -
    (titleHeight + TITLE_DATE_GAP_PX * scale + dateText.sizePx * LINE_HEIGHT) /
      2
  const fit = (text: string, style: typeof TITLE_TEXT): string =>
    fitKeyRow(text, style.sizePx, style.weight, measure, titleMaxPx)

  return {
    top,
    width,
    height,
    title: textLine(
      fit(copy.title, titleText),
      titleText,
      paddingPx,
      titleTop,
      titleMaxPx,
    ),
    date: textLine(
      fit(copy.date, dateText),
      dateText,
      paddingPx,
      titleTop + titleHeight + TITLE_DATE_GAP_PX * scale,
      titleMaxPx,
    ),
    legend: legend.items,
    scaleBar,
    northArrow,
  }
}
